import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { apiConfigs } from '../../../../api/APIConfigs';
import { BibleScripture } from '../../../../api/models/BibleVerse';
import {
  generateBibleRangePayloadFromScriptureIDs,
  getScriptureIdsFromBibleScriptures,
  normalizeScriptureTupleWithBooksCatalog,
  resolveBookIdToCatalogId,
} from '../../../../util/bible-study-module';
import { getSelectValue } from '../../../../util/get-select-value';
import { useAsyncRequest } from '../../../../util/use-async-request';
import { useRequest } from '../../../../util/use-request';
import { RhButton, RhButtonMode } from '../../../core/RhButton';
import { RhSelect, RhSelectOption } from '../../../core/RhSelect';
import { SizedBox } from '../../../core/SizedBox';
import { colors, Description, H2 } from '../../../core/Styles';

type Props = {
  initialLoading?: boolean;
  initialForm?: { book: string; from: ChapterRange; to: ChapterRange };
  translationId?: string | null;
  isDeleteButtonHidden?: boolean;
  onDeleteButtonClick?: () => void;
  onResolvedTranslationId?: (translationId: string) => void;
  /** Invoked when user selects all dropdowns completely */
  onChange: (scriptureIds: string[]) => void;
};

type ChapterRange = { chapter: number; verse: number };

const initChapterRange: ChapterRange = { chapter: 0, verse: 0 };

const parseSelectedVersion = (value: string | null | undefined): { bibleId: string; translationId: string } | null => {
  if (!value) return null;
  if (!value.includes('|')) return { bibleId: value, translationId: '' };
  const [bibleId, translationId] = value.split('|');
  if (!bibleId) return null;
  return { bibleId, translationId: translationId || '' };
};

type BookStructure = {
  book: string;
  chapters: number;
  verses: Array<{ chapter: string; maxVerse: number }>;
};

type BibleVersion = { id: string; value: string; name: string };

type BibleBookRow = {
  id: string;
  bibleId?: string;
  abbreviation?: string;
  name?: string;
  nameLong?: string;
};

const getBookId = (selectedBook: string | null, bookOptions: RhSelectOption[]): string | null => {
  if (!selectedBook) return null;
  const byValue = bookOptions.find(option => option.value === selectedBook)?.value;
  if (byValue) return byValue;
  const byLabel = bookOptions.find(option => option.label === selectedBook)?.value;
  return byLabel || null;
};

/**
 * Oh, boy! This component got big.
 * TODO: we might need to do some SOC here
 * @param props
 * @returns
 */
export const ScriptureModuleForm = (props: Props) => {
  const { onChange, translationId } = props;

  const translationIdRef = useRef<string | null | undefined>(props.translationId);

  const [selectedBook, setSelectedBook] = useState<string | null>(props.initialForm?.book || null);
  const [from, setFrom] = useState<ChapterRange>(props.initialForm?.from || initChapterRange);
  const [to, setTo] = useState<ChapterRange>(props.initialForm?.to || initChapterRange);
  const [gettingScriptures, setGettingScriptures] = useState(props.initialLoading || false);
  const [selectedScriptures, setSelectedScriptures] = useState<BibleScripture[]>([]);
  const [bookOptions, setBookOptions] = useState<RhSelectOption[]>([]);
  const [effectiveVersion, setEffectiveVersion] = useState<{ bibleId: string; translationId: string } | null>(null);

  const request = useRequest();
  const selectedVersion = useMemo(() => parseSelectedVersion(translationId), [translationId]);
  const selectedBookId = useMemo(() => getBookId(selectedBook, bookOptions), [selectedBook, bookOptions]);

  // Load options for bible books if user selected a version
  const { loading: loadingBooks } = useAsyncRequest(
    useCallback(() => {
      return effectiveVersion
        ? request<any[]>(apiConfigs.getBibleBooks(effectiveVersion.bibleId))
        : Promise.resolve(null);
    }, [effectiveVersion, request]),
    useCallback(result => {
      const books = (result || []) as BibleBookRow[];
      setBookOptions(
        books.map(b => {
          const title = b.nameLong || b.name || b.id;
          return { label: `${title} (${b.id})`, value: b.id };
        })
      );
    }, [])
  );

  useEffect(() => {
    if (!bookOptions.length || !selectedBook) return;
    if (bookOptions.some(o => o.value === selectedBook)) return;
    const catalog = bookOptions.map(o => ({ id: o.value }));
    const resolved = resolveBookIdToCatalogId(selectedBook, catalog);
    if (resolved !== selectedBook && bookOptions.some(o => o.value === resolved)) {
      setSelectedBook(resolved);
    }
  }, [bookOptions, selectedBook]);

  // load bible book structure if user selected a book
  const { loading: loadingBookInfo, data: bookInfo } = useAsyncRequest(
    useCallback(() => {
      return selectedBookId && effectiveVersion
        ? request<BookStructure>(apiConfigs.getBibleBookStructure(effectiveVersion.bibleId, selectedBookId))
        : Promise.resolve(null);
    }, [effectiveVersion, request, selectedBookId])
  );

  /**
   * Listens to changes on the bible versions
   * and reset all the states
   */
  useEffect(() => {
    if (translationIdRef.current !== translationId) {
      setSelectedBook(null);
      setSelectedScriptures([]);
      setBookOptions([]);
      setEffectiveVersion(null);
      setFrom(initChapterRange);
      setTo(initChapterRange);
      translationIdRef.current = translationId;
    }
  }, [translationId]);

  useEffect(() => {
    const resolveVersion = async () => {
      if (!translationId) {
        setEffectiveVersion(null);
        return;
      }

      if (translationId.includes('|')) {
        setEffectiveVersion(parseSelectedVersion(translationId));
        return;
      }

      try {
        const versions = await request<BibleVersion[]>(apiConfigs.getBibleVersions());
        const match = (versions || []).find(v => v.id === translationId || v.value === translationId);
        if (match) {
          setEffectiveVersion({ bibleId: match.id, translationId: match.id });
          if (translationId !== match.id) {
            props.onResolvedTranslationId?.(match.id);
          }
          return;
        }
      } catch {
        toast.error('Failed to get translation');
      }

      setEffectiveVersion({ bibleId: translationId, translationId: '' });
    };

    resolveVersion();
  }, [request, translationId]);

  /**
   * Listen to changes in dropdowns,
   * and update scripture selection accordingly
   */
  useEffect(() => {
    const getScriptures = async () => {
      if (selectedBookId && from.chapter && from.verse && to.chapter && to.verse && effectiveVersion) {
        const chapterSpan = to.chapter - from.chapter;
        if (chapterSpan > 4) {
          toast.error('This request exceeds the supported verse limit; please break it into smaller segments');
        }
        try {
          setGettingScriptures(true);
          const scriptureTuple = [
            selectedBookId,
            String(from.chapter),
            String(from.verse),
            '-',
            selectedBookId,
            String(to.chapter),
            String(to.verse),
          ];
          const catalog = bookOptions.map(o => ({ id: o.value }));
          const normalizedTuple = normalizeScriptureTupleWithBooksCatalog(scriptureTuple, catalog);
          const rangePayload = generateBibleRangePayloadFromScriptureIDs(normalizedTuple);
          const verses = await request<BibleScripture[]>(
            apiConfigs.getBibleRange({
              ...rangePayload,
              version: effectiveVersion.translationId,
            })
          );
          setGettingScriptures(false);
          setSelectedScriptures(verses);
        } catch (e: any) {
          toast.error('Failed to get scripture range');
        }
      }
    };

    // Add small debounce for the state to reset when a new version is selected
    const delayDebounceFn = setTimeout(() => {
      getScriptures();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [
    bookOptions,
    effectiveVersion,
    from.chapter,
    from.verse,
    request,
    selectedBookId,
    to.chapter,
    to.verse,
    translationId,
  ]);

  /**
   * Listen to changes in selectedScriptures
   * and return result to props.onChange
   */
  useEffect(() => {
    onChange(
      getScriptureIdsFromBibleScriptures(selectedScriptures, selectedBookId, {
        fromChapter: from.chapter,
        fromVerse: from.verse,
        toChapter: to.chapter,
        toVerse: to.verse,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedScriptures), selectedBookId, from.chapter, from.verse, to.chapter, to.verse]);

  /**
   * Get verses by chapter
   * param: chapter cannot be 0
   */
  const getVerseList = useCallback(
    (chapter: number) => {
      if (!bookInfo || !chapter) return [];
      const verseMeta = bookInfo.verses.find(v => v.chapter === String(chapter));
      const maxVerse = verseMeta?.maxVerse || 0;
      return maxVerse
        ? [...new Array(maxVerse)].map((_, index) => ({
            label: index + 1 + '',
            value: index + 1 + '',
          }))
        : [];
    },
    [bookInfo]
  );

  // The following are listeners for when user selects a new value from one of the dropdowns

  const fromChapters = useMemo(() => {
    return bookInfo
      ? [...new Array(bookInfo.chapters)].map((_, index) => ({
          label: index + 1 + '',
          value: index + 1 + '',
        }))
      : [];
  }, [bookInfo]);

  const fromVerses = useMemo(() => {
    return from.chapter ? getVerseList(from.chapter) : [];
  }, [from.chapter, getVerseList]);

  const toChapters = useMemo(() => {
    return bookInfo && from.chapter ? fromChapters.slice(from.chapter - 1) : [];
  }, [bookInfo, from.chapter, fromChapters]);

  const toVerses = useMemo(() => {
    if (from.chapter === to.chapter) {
      return fromVerses.slice(from.verse - 1);
    }
    return getVerseList(to.chapter);
  }, [from.chapter, from.verse, fromVerses, getVerseList, to.chapter]);

  // The following are handlers for when user updates to & from fields.
  // The idea is:
  // If a field is updated, and the value is not the same as the previous, reset subsequent fields

  const userSelectedBook = (book: string) => {
    if (selectedBook !== book) {
      setSelectedBook(book);
      setFrom(initChapterRange);
      setTo(initChapterRange);
      setSelectedScriptures([]);
    }
  };

  const userSelectedFromChapter = (chapterStr: string) => {
    const chapter = parseInt(chapterStr);
    if (from.chapter !== chapter) {
      setFrom(f => ({ ...f, chapter, verse: 0 }));
      setTo(initChapterRange);
      setSelectedScriptures([]);
    }
  };

  const userSelectedFromVerse = (verseStr: string) => {
    const verse = parseInt(verseStr);
    if (from.verse !== verse) {
      setFrom(f => ({ ...f, verse }));
      setTo(f => ({ ...f, verse: 0 }));
      setSelectedScriptures([]);
    }
  };

  const userSelectedToChapter = (chapterStr: string) => {
    const chapter = parseInt(chapterStr);
    if (to.chapter !== chapter) {
      setTo(f => ({ ...f, chapter, verse: 0 }));
      setSelectedScriptures([]);
    }
  };

  return (
    <ScriptureDiv>
      <div>
        <H2>Book</H2>
        <SizedBox height={8} />
        <RhSelect
          value={getSelectValue(selectedBook)}
          onChange={e => userSelectedBook(e.value)}
          isLoading={loadingBooks}
          isDisabled={loadingBooks || gettingScriptures || !props.translationId}
          options={bookOptions}
        />
        <SizedBox height={20} />
        <H2>From</H2>
        <SizedBox height={8} />
        <Selects>
          {/* from chapters */}
          <RhSelect
            value={getSelectValue(from.chapter)}
            onChange={e => userSelectedFromChapter(e.value)}
            options={fromChapters}
            isLoading={loadingBookInfo}
            isDisabled={
              !bookOptions.length ||
              !bookInfo ||
              loadingBookInfo ||
              loadingBooks ||
              !fromChapters.length ||
              gettingScriptures ||
              !selectedBook
            }
          />
          {/* from verses */}
          <RhSelect
            value={getSelectValue(from.verse)}
            onChange={e => userSelectedFromVerse(e.value)}
            isLoading={loadingBookInfo}
            options={fromVerses}
            isDisabled={
              !bookOptions.length ||
              !bookInfo ||
              loadingBookInfo ||
              loadingBooks ||
              !from.chapter ||
              !fromVerses.length ||
              gettingScriptures ||
              !selectedBook
            }
          />
        </Selects>
        <SizedBox height={20} />
        <H2>To</H2>
        <SizedBox height={8} />
        <Selects>
          {/* to chapters */}
          <RhSelect
            value={getSelectValue(to.chapter)}
            onChange={e => userSelectedToChapter(e.value)}
            isLoading={loadingBookInfo}
            options={toChapters}
            isDisabled={
              !bookOptions.length ||
              !bookInfo ||
              loadingBookInfo ||
              loadingBooks ||
              !from.chapter ||
              !from.verse ||
              !toChapters.length ||
              gettingScriptures ||
              !selectedBook
            }
          />
          {/* to verses */}
          <RhSelect
            value={getSelectValue(to.verse)}
            onChange={e => setTo(f => ({ ...f, verse: parseInt(e.value) }))}
            isLoading={loadingBookInfo}
            options={toVerses}
            isDisabled={
              !bookOptions.length ||
              !bookInfo ||
              loadingBookInfo ||
              loadingBooks ||
              !to.chapter ||
              !toVerses.length ||
              gettingScriptures ||
              !selectedBook
            }
          />
        </Selects>
      </div>
      <div>
        <ScriptureTExt>
          {selectedBook ? (
            <H2>
              {selectedBook} {!!from.chapter && from.chapter}
              {!!from.verse && `:${from.verse}`}
              {!!to.chapter && `-${to.chapter}`}
              {!!to.verse && `:${to.verse}`}
            </H2>
          ) : (
            <H2 color={colors.grey}>Book, Chapter, Verse</H2>
          )}
          <SizedBox height={16} />
          {gettingScriptures ? (
            <Description>Loading...</Description>
          ) : !!selectedScriptures.length ? (
            <Scriptures>
              {selectedScriptures.map(scripture => (
                <Description key={scripture.id}>
                  <sup style={{ fontSize: 10 }}>{scripture.verse}</sup> {scripture.text}
                </Description>
              ))}
            </Scriptures>
          ) : (
            <Description color={colors.grey}>Scripture</Description>
          )}
        </ScriptureTExt>
        <SizedBox height={10} />
        {!props.isDeleteButtonHidden && (
          <AlignRight>
            <RhButton
              mode={RhButtonMode.subtle}
              prefixIcon={FiTrash2}
              iconSize={18}
              onClick={props.onDeleteButtonClick}
            />
          </AlignRight>
        )}
      </div>
    </ScriptureDiv>
  );
};

const ScriptureDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  grid-gap: 29px;
`;

const Selects = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 20px;
`;

// const EditButtonDiv = styled.div`
//   display: flex;
//   justify-content: flex-end;
// `;

const ScriptureTExt = styled.div`
  border-radius: 10px;
  border: 1px solid #e2e7ea;
  outline: none;
  padding: 20px;
  max-height: 40vh;
  overflow-y: scroll;
`;

const Scriptures = styled.div`
  & > :not(:last-child) {
    margin-bottom: 16px;
  }
`;

const AlignRight = styled.div`
  display: flex;
  justify-content: flex-end;
`;
